import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FaBuilding, FaUser, FaEnvelope, FaPhone, FaArrowLeft, FaDownload } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import superadminAPI from '../../utils/superadminAPI';

const CreateCompanyAdmin = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [validated, setValidated] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess(false);
      setPdfData(null);
      
      const response = await superadminAPI.createCompanyAdmin(formData);
      
      setSuccess(true);
      setPdfData(response.data.pdfBuffer);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: ''
      });
      setValidated(false);
    } catch (error) {
      console.error('Error creating company admin:', error);
      setError(error.response?.data?.message || 'Failed to create company admin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!pdfData) return;
    
    // Convert base64 to blob
    const byteCharacters = atob(pdfData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `credentials_${new Date().getTime()}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col>
          <Button 
            variant="outline-primary" 
            as={Link} 
            to="/superadmin/company-admins"
            className="mb-3"
          >
            <FaArrowLeft className="me-2" /> Back to Company Admins
          </Button>
          <h1 className="mb-2">Create Company Admin</h1>
          <p className="text-muted mb-0">Add a new company administrator to the FieldFlow platform.</p>
        </Col>
      </Row>

      <Row>
        <Col lg={8} xl={9}>
          <Card className="shadow-sm">
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && (
                <Alert variant="success" className="d-flex justify-content-between align-items-center flex-column flex-md-row">
                  <div className="mb-2 mb-md-0">Company admin created successfully! Credentials have been generated.</div>
                  <Button 
                    variant="outline-success" 
                    size="sm"
                    onClick={handleDownloadPdf}
                    className="d-flex align-items-center w-100 w-md-auto"
                  >
                    <FaDownload className="me-2" /> Download Credentials
                  </Button>
                </Alert>
              )}
              
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaBuilding className="me-2 text-primary" />
                        Company Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Enter company name"
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Company name is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaUser className="me-2 text-primary" />
                        Admin Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter admin name"
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Admin name is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaEnvelope className="me-2 text-primary" />
                        Email
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter email address"
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid email address
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaPhone className="me-2 text-primary" />
                        Phone (Optional)
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter phone number"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end flex-column flex-md-row gap-2">
                  <Button 
                    variant="secondary" 
                    className="w-100 w-md-auto"
                    as={Link}
                    to="/superadmin/company-admins"
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit"
                    disabled={loading}
                    className="w-100 w-md-auto"
                  >
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                        Creating...
                      </>
                    ) : (
                      'Create Company Admin'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} xl={3} className="mt-4 mt-lg-0">
          <Card className="shadow-sm bg-light">
            <Card.Body>
              <h5 className="mb-3">Important Information</h5>
              <p>
                When you create a new company admin:
              </p>
              <ul className="small">
                <li>A secure random password will be automatically generated</li>
                <li>The admin's credentials will be available for download as a PDF</li>
                <li>The company admin will have access to manage their own personnel and sites</li>
                <li>Each company's data is kept separate from other companies</li>
              </ul>
              <p className="mb-0 text-muted">
                <small>
                  Make sure to securely share the credentials PDF with the company admin.
                </small>
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreateCompanyAdmin;
